import {
  DermloopClinicalActionTypeEnum,
  DermloopInvitationRoles,
  DermloopRoleStatus,
  DermloopUserAffiliationDeleteReasonEnum,
  DermloopUserRoleEnum,
} from '@dermloop/static-values';
import { Exclude, Transform } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayMinSize,
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsDate,
  IsEmail,
  IsEnum,
  IsIn,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  MinDate,
  ValidateIf,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from './base.dto';
import type { ConditionTypeDto } from './condition-type.dtos';
import { FederationDto } from './federation.dtos';
import { JurisdictionalGroupDto } from './jurisdictional-group.dtos';
import { MedicalUnitGroupDto } from './medical-unit-group.dtos';
import { MedicalUnitDto } from './medical-unit.dtos';
import { OccupationDto } from './occupation.dtos';
import { SpecialisationDto } from './specialisation.dtos';
import { UserDto } from './user.dto';

export interface UserAffiliationDto extends BaseDto {
  user?: UserDto;

  userId?: string;

  roles?: DermloopUserRoleEnum[];

  readonly?: boolean;

  expiry?: string | Date | null;

  occupation?: OccupationDto;

  occupationId?: string;

  specialisation?: SpecialisationDto;

  specialisationId?: string;

  /** @deprecated Use specialisation */
  speciality?: SpecialisationDto;

  /** @deprecated Use specialisationId */
  specialityId?: string;

  allowedClinicalActions?: DermloopClinicalActionTypeEnum[];

  deleteReason?: DermloopUserAffiliationDeleteReasonEnum;

  lastActiveDate?: Date;

  medicalUnit?: MedicalUnitDto;

  medicalUnitId?: string;

  medicalUnitGroup?: MedicalUnitGroupDto;

  medicalUnitGroupId?: string;

  jurisdictionalGroup?: JurisdictionalGroupDto;

  jurisdictionalGroupId?: string;

  federation?: FederationDto;

  federationId?: string;

  email?: string;

  invitationAccepted: boolean;

  invitationExpiryDate: Date;

  invitationCode?: string;

  invitationNpi?: string;

  /** True when this affiliation is exempt from the 90-day inactivity deactivation (e.g. exempt role or 1Login user). */
  isExemptFromDeactivation?: boolean;

  // @deprecated use roles instead. Can be removed after April 1st, 2026 (issue #6271)
  invitationRole?: DermloopUserRoleEnum;

  /**
   * Populated only by user-affiliations `search`/`GET :id`/`POST`/`PUT :id`;
   * absent from every other endpoint returning this DTO, including `GET`
   * (own affiliations) and `GET current`. Named distinctly from the entity's
   * own `conditionTypes` relation (the raw grant rows) since this carries
   * both the grant and its resolved effective value.
   */
  conditionTypeGrant?: UserAffiliationConditionTypesDto;
}

export class CreateUserAffiliationDto
  extends BaseCreateDto
  implements Partial<UserAffiliationDto>
{
  @Exclude()
  user?: never;

  @IsOptional()
  @IsUUID('all')
  userId?: string;

  // Provide at least 1 role
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(100)
  @IsEnum(DermloopUserRoleEnum, { each: true })
  roles?: DermloopUserRoleEnum[];

  @IsOptional()
  @IsBoolean()
  readonly?: boolean;

  @IsOptional()
  @IsDate()
  @MinDate(() => new Date())
  expiry?: Date;

  @Exclude()
  occupation?: never;

  @IsOptional()
  @IsUUID()
  occupationId?: string;

  @Exclude()
  specialisation?: never;

  /** @deprecated Use specialisation (excluded on write) */
  @Exclude()
  speciality?: never;

  @IsOptional()
  @IsUUID()
  specialisationId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use specialisationId */
  specialityId?: string;

  @IsArray()
  @ArrayNotEmpty()
  @IsEnum(DermloopClinicalActionTypeEnum, { each: true })
  allowedClinicalActions: DermloopClinicalActionTypeEnum[];

  @Exclude()
  deleteReason?: never;

  @Exclude()
  medicalUnit?: never;

  // Must be set if user is site manager and can add to several medical units
  @IsOptional()
  @IsUUID()
  medicalUnitId?: string;

  @Exclude()
  medicalUnitGroup?: never;

  @Exclude()
  medicalUnitGroupId?: never;

  @Exclude()
  jurisdictionalGroup?: never;

  @Exclude()
  jurisdictionalGroupId?: never;

  @Exclude()
  federation?: never;

  @Exclude()
  federationId?: never;

  @IsEmail()
  @Transform(({ value }) => value?.toLowerCase())
  email: string;

  @Exclude()
  invitationAccepted?: never;

  @Exclude()
  invitationExpiryDate?: never;

  @Exclude()
  invitationCode?: never;

  @IsOptional()
  @IsString()
  invitationNpi?: string;

  // An empty list clears the grant, so the affiliation inherits its medical
  // unit's condition types. Omitted entirely leaves the grant unset (inherit).
  // ValidateIf, not IsOptional: null must still fail IsArray rather than be
  // treated as omitted.
  @ValidateIf((_, value) => value !== undefined)
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];
}

export class UpdateUserAffiliationDto
  extends BaseUpdateDto
  implements Partial<UserAffiliationDto>
{
  @Exclude()
  user?: never;

  @Exclude()
  userId?: never;

  // Replaces only the affiliation's invitable roles; its other roles are kept.
  @ValidateIf((_, value) => value !== undefined)
  @IsArray()
  @ArrayMinSize(1)
  @IsIn(DermloopInvitationRoles, { each: true })
  roles?: DermloopUserRoleEnum[];

  @IsOptional()
  @IsBoolean()
  readonly?: boolean;

  @IsOptional()
  @IsDate()
  @MinDate(() => new Date())
  expiry?: Date;

  @Exclude()
  occupation?: never;

  @IsOptional()
  @IsUUID()
  occupationId?: string;

  @Exclude()
  specialisation?: never;

  /** @deprecated Use specialisation (excluded on write) */
  @Exclude()
  speciality?: never;

  @IsOptional()
  @IsUUID()
  specialisationId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use specialisationId */
  specialityId?: string;

  @IsOptional()
  @IsArray()
  @IsEnum(DermloopClinicalActionTypeEnum, { each: true })
  allowedClinicalActions?: DermloopClinicalActionTypeEnum[];

  @Exclude()
  deleteReason?: never;

  @Exclude()
  medicalUnit?: never;

  @Exclude()
  medicalUnitId?: never;

  @Exclude()
  medicalUnitGroup?: never;

  @Exclude()
  medicalUnitGroupId?: never;

  @Exclude()
  jurisdictionalGroup?: never;

  @Exclude()
  jurisdictionalGroupId?: never;

  @Exclude()
  federation?: never;

  @Exclude()
  federationId?: never;

  @Exclude()
  email?: never;

  @Exclude()
  invitationAccepted?: never;

  @Exclude()
  invitationExpiryDate?: never;

  @Exclude()
  invitationCode?: never;

  @IsOptional()
  @IsString()
  invitationNpi?: string;

  // SiteManager can update user phone number and names
  @IsOptional()
  @IsString()
  userFirstName?: string;

  @IsOptional()
  @IsString()
  userLastName?: string;

  @IsOptional()
  @IsString()
  userPhoneNumber?: string;

  @IsOptional()
  @IsString()
  userPhoneNumberPrefix?: string;

  // An empty list clears the grant, so the affiliation inherits its medical
  // unit's condition types. Omitted entirely leaves the existing grant as-is.
  // ValidateIf, not IsOptional: null must still fail IsArray rather than be
  // treated as omitted.
  @ValidateIf((_, value) => value !== undefined)
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];
}

export class UpdateUserAffiliationConditionTypesDto {
  // An empty list clears the grant, so the affiliation inherits its medical
  // unit's condition types.
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  conditionTypeIds: string[];
}

/**
 * Manager/Admin counterpart of `UpdateUserAffiliationDto` for the fields the
 * edit-affiliation sidebar saves. Omitted fields are left as-is.
 */
export class UpdateUserAffiliationAccessDto {
  @ValidateIf((_, value) => value !== undefined)
  @IsArray()
  @ArrayMinSize(1)
  @IsIn(DermloopInvitationRoles, { each: true })
  roles?: DermloopUserRoleEnum[];

  @IsOptional()
  @IsArray()
  @IsEnum(DermloopClinicalActionTypeEnum, { each: true })
  allowedClinicalActions?: DermloopClinicalActionTypeEnum[];

  @ValidateIf((_, value) => value !== undefined)
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];
}

export interface UserAffiliationConditionTypesDto {
  /** The affiliation's own grant. Empty when the affiliation inherits. */
  granted: ConditionTypeDto[];

  /** In effect for the clinician: the grant when it has rows, otherwise the medical unit's own rows. */
  effective: ConditionTypeDto[];
}

export interface DeleteUserAffiliationResultDto {
  success: boolean;
}

export class SearchUserAffiliationDto {
  @IsOptional()
  @IsEnum(DermloopUserRoleEnum, { each: true })
  roles?: DermloopUserRoleEnum[];

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  medicalUnitIds?: string[];

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  jurisdictionalGroupIds?: string[];

  // If true, filters to only deleted users
  @IsOptional()
  @IsBoolean()
  deleted?: boolean;

  @IsOptional()
  @IsBoolean()
  withDeleted?: boolean;

  // If true, filters to expiring users
  @IsOptional()
  @IsBoolean()
  expiringUsers?: boolean;

  /** Used to search for invitations/affiliations by email or name  */
  @IsOptional()
  @IsString()
  searchQuery?: string;

  /**
   * Filter for the affiliation status.
   * {@link DermloopRoleStatus}
   * */
  @IsOptional()
  @IsEnum(DermloopRoleStatus, { each: true })
  status?: DermloopRoleStatus[];

  // If true, filters to users that have accepted their invitation
  @IsOptional()
  @IsBoolean()
  @ValidateIf((o) => !o.onlyPendingInvitations)
  onlyAcceptedInvitations?: boolean;

  @IsOptional()
  @IsBoolean()
  @ValidateIf((o) => !o.onlyAcceptedInvitations)
  onlyPendingInvitations?: boolean;

  @IsOptional()
  @IsUUID('all', { each: true })
  userIds?: string[];

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(100)
  take?: number;

  @IsOptional()
  @IsBoolean()
  sortResults?: boolean;
}
